/**
 * Background-mode rules for the Pose 3D editor.
 *
 * These live outside the component so they can be tested directly: the editor is
 * a .vue single-file component and the unit runner has no vue transform, so
 * anything left inline could only be asserted against as raw source text.
 */

export const POSE_3D_BG_MODE = {
	TRANSPARENT: 'TRANSPARENT',
	COLOR: 'COLOR',
	IMAGE: 'IMAGE',
} as const;

export type Pose3DBgMode = typeof POSE_3D_BG_MODE[keyof typeof POSE_3D_BG_MODE];

// Order matters: it is the order the buttons appear in the toggle, and the
// toggle reports the chosen button by index.
export const POSE_3D_BG_MODES: Pose3DBgMode[] = [
	POSE_3D_BG_MODE.TRANSPARENT,
	POSE_3D_BG_MODE.COLOR,
	POSE_3D_BG_MODE.IMAGE,
];

export function bgModeToIndex (mode: Pose3DBgMode): number {
	const index = POSE_3D_BG_MODES.indexOf(mode);
	return index === -1 ? 0 : index;
}

export function bgModeFromIndex (index: number): Pose3DBgMode {
	return POSE_3D_BG_MODES[index] || POSE_3D_BG_MODE.TRANSPARENT;
}

/**
 * Whether the Babylon scene clears to alpha. Image mode renders the model over
 * nothing exactly like transparent mode does, because the chosen image is
 * composited behind it afterwards rather than drawn into the scene.
 */
export function usesTransparentScene (mode: Pose3DBgMode): boolean {
	return mode !== POSE_3D_BG_MODE.COLOR;
}

/**
 * The background image to composite into a capture, or null for a capture that
 * needs no compositing. Image mode with nothing chosen yet degrades to a plain
 * transparent capture rather than failing.
 */
export function resolveCaptureBackgroundImageUrl (mode: Pose3DBgMode, bgImageUrl: string | null): string | null {
	return mode === POSE_3D_BG_MODE.IMAGE ? (bgImageUrl || null) : null;
}

/**
 * The live preview half of the capture contract. `cover` + `center` is what
 * computeCoverDrawRect reproduces when compositing, so the two agree as long as
 * both describe the same square.
 */
export function buildCanvasBackgroundStyle (mode: Pose3DBgMode, bgImageUrl: string | null): Record<string, string> {
	if (mode !== POSE_3D_BG_MODE.IMAGE || !bgImageUrl) return {};
	return {
		backgroundImage: `url(${bgImageUrl})`,
		backgroundSize: 'cover',
		backgroundPosition: 'center',
		backgroundRepeat: 'no-repeat',
	};
}

/**
 * Where to land when a chosen background image cannot be loaded.
 *
 * Staying in image mode is only honest when a previously loaded image is still
 * there to fall back on. With nothing to show, image mode would render no
 * checkerboard and capture a silently transparent PNG, so the editor drops back
 * to transparent instead.
 */
export function resolveBgModeAfterImageFailure (currentBgImageUrl: string | null): Pose3DBgMode {
	return currentBgImageUrl ? POSE_3D_BG_MODE.IMAGE : POSE_3D_BG_MODE.TRANSPARENT;
}
